import React, { useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Alert } from '@mui/material';
import * as z from 'zod';

import Button from '../../common/Button';
import { type TypeProject } from '../../common/dataTypes';
import { extractValue } from '../../common/utils';
import { useCreateProject } from '../../hooks';
import { useUpdateProject } from '../../hooks/tanstack/useProjectsHook';
import {
  useGetRoles,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { handleQueryError } from '../../utils/api';
import FormInput from '../form/FormInput';

const CreateProjectSchema = z.object({
  name: z.string().trim().trim().min(1, 'Project name is required'),
  description: z.string().trim().optional(),
});

type CreateProjectSchemaType = z.infer<typeof CreateProjectSchema>;

type CreateProjectFormProps = {
  primaryActionLabel?: string;
  handleOnCancel?: () => void;
  handleOnLoading?: (loading: boolean) => void;
  handleOnSubmit?: (
    data:
      | TypeProject
      | {
          id: number;
        }
  ) => void;
  defaultValues?: CreateProjectSchemaType & {
    id: number;
  };
};

const CreateProjectForm = ({
  handleOnSubmit,
  handleOnCancel,
  handleOnLoading,
  defaultValues,
  primaryActionLabel,
}: CreateProjectFormProps) => {
  const [error, setError] = useState(Object);

  const errorMessage = extractValue(error);

  const { orgId } = useGetRouteParams();

  const form = useForm<CreateProjectSchemaType>({
    resolver: zodResolver(CreateProjectSchema),
    defaultValues,
  });

  const { handleSubmit, reset, formState } = form;

  const { isDirty } = formState;

  const { projectRole } = useGetRoles();

  const { mutateAsync: createProject, isPending: isCreatingProject } =
    useCreateProject({
      onSuccess(data) {
        handleOnSubmit?.(data);
        handleOnLoading?.(false);
      },
      onError: (error) => {
        handleQueryError(error, setError);
        handleOnLoading?.(false);
      },
    });

  const { mutateAsync: updateProject, isPending: isUpdatingProject } =
    useUpdateProject({
      onSuccess(data) {
        handleOnSubmit?.(data);
        handleOnLoading?.(false);
      },
      onError: (error) => {
        handleQueryError(error, setError);
        handleOnLoading?.(false);
      },
    });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const handleCreateProject: SubmitHandler<CreateProjectSchemaType> = async (
    data
  ) => {
    if (!isDirty && defaultValues) {
      handleOnSubmit?.({
        id: defaultValues.id,
      });
      return;
    }

    if (!orgId) {
      return;
    }

    handleOnLoading?.(true);

    if (defaultValues) {
      await updateProject({
        projectId: defaultValues.id,
        orgId,
        data: {
          ...data,
          organization: orgId,
        },
      });

      return;
    }

    await createProject({
      orgId,
      data: {
        ...data,
        organization: orgId,
      },
    });
  };

  const isDisabled =
    (isCreatingProject ?? isUpdatingProject) || projectRole
      ? projectRole !== 'project_owner'
      : false;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(handleCreateProject)}
        className="space-y-4 px-6 pb-4"
      >
        <FormInput
          label="Name"
          inputProps={{
            maxLength: 100,
          }}
          disabled={isDisabled}
          name="name"
        />
        <FormInput
          label="Description"
          multiline
          rows={4}
          disabled={isDisabled}
          placeholder="(Optional)"
          name="description"
        />
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <div className="flex justify-end gap-4">
          <Button
            disabled={isUpdatingProject || isCreatingProject}
            onClick={handleOnCancel}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            loading={isUpdatingProject || isCreatingProject}
            type="submit"
            disabled={isDisabled}
            variant="contained_blue"
          >
            {primaryActionLabel ??
              (defaultValues
                ? isDirty
                  ? 'Update'
                  : 'Next'
                : 'Create project')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateProjectForm;
